import type { QueryClient, QueryKey } from "@tanstack/react-query";
import type { Locale } from "@/lib/i18n";
import type { SellerSession } from "@/lib/api/types";
import { queryKeys } from "@/shared/queries";
import {
  RESTAURANT_NOTIFICATION_TYPES,
  isRestaurantNotificationType,
  type RestaurantNotificationType,
} from "@/lib/firebase/notification-types";

/** One of the four operational events — each about one bill. */
export type RestaurantInvoiceNotificationData = {
  type: Exclude<
    RestaurantNotificationType,
    | typeof RESTAURANT_NOTIFICATION_TYPES.dayClosed
    | typeof RESTAURANT_NOTIFICATION_TYPES.approvalUsed
    | typeof RESTAURANT_NOTIFICATION_TYPES.onlineOrderRequested
  >;
  storeId: string;
  invoiceId: string;
  itemCount?: number;
  ticketCount?: number;
};

/** D151 — the nightly number: about a business day, not a bill. */
export type RestaurantDayClosedNotificationData = {
  type: typeof RESTAURANT_NOTIFICATION_TYPES.dayClosed;
  storeId: string;
  businessDate: string;
};

/**
 * D156 — the approver's notice: about one bill, and the act on it. The facts
 * beside the id let the bell re-word it in the reader's language.
 */
export type RestaurantApprovalNotificationData = {
  type: typeof RESTAURANT_NOTIFICATION_TYPES.approvalUsed;
  storeId: string;
  invoiceId: string;
  commandType?: ApprovalNotificationAct;
  dailySequence?: number;
  localTime?: string;
  tillName?: string;
};

/**
 * 2026-10-20 — a storefront takeaway/delivery request: about a REQUEST, not
 * yet a bill. Its channel titles it; the plate count words the body.
 */
export type RestaurantOnlineOrderNotificationData = {
  type: typeof RESTAURANT_NOTIFICATION_TYPES.onlineOrderRequested;
  storeId: string;
  onlineOrderId: string;
  channel?: "TAKEAWAY" | "DELIVERY";
  itemCount?: number;
};

export type ApprovalNotificationAct =
  | "voidItem"
  | "compItem"
  | "applyBillDiscount"
  | "refundInvoice";

const APPROVAL_ACTS = new Set<string>([
  "voidItem",
  "compItem",
  "applyBillDiscount",
  "refundInvoice",
]);

export type RestaurantNotificationData =
  | RestaurantInvoiceNotificationData
  | RestaurantDayClosedNotificationData
  | RestaurantApprovalNotificationData
  | RestaurantOnlineOrderNotificationData;

function positiveInteger(value: unknown): number | undefined {
  if (typeof value !== "string" || !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

/**
 * Treat push/Firestore data as untrusted input. Routes are derived only from
 * this exact allowlist and required operational ids; a payload URL is never
 * accepted or navigated to.
 */
export function parseRestaurantNotificationData(
  value: unknown,
): RestaurantNotificationData | null {
  if (!value || typeof value !== "object") return null;
  const data = value as Record<string, unknown>;
  if (
    data.type === RESTAURANT_NOTIFICATION_TYPES.dayClosed &&
    typeof data.storeId === "string" &&
    data.storeId.length > 0 &&
    typeof data.businessDate === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(data.businessDate)
  ) {
    return {
      type: RESTAURANT_NOTIFICATION_TYPES.dayClosed,
      storeId: data.storeId,
      businessDate: data.businessDate,
    };
  }
  if (
    data.type === RESTAURANT_NOTIFICATION_TYPES.approvalUsed &&
    typeof data.storeId === "string" &&
    data.storeId.length > 0 &&
    typeof data.invoiceId === "string" &&
    data.invoiceId.length > 0
  ) {
    // Every fact is optional and checked: a malformed one is dropped, and the
    // copy falls back to the server's own words.
    return {
      type: RESTAURANT_NOTIFICATION_TYPES.approvalUsed,
      storeId: data.storeId,
      invoiceId: data.invoiceId,
      commandType:
        typeof data.commandType === "string" && APPROVAL_ACTS.has(data.commandType)
          ? (data.commandType as ApprovalNotificationAct)
          : undefined,
      dailySequence: positiveInteger(data.dailySequence),
      localTime:
        typeof data.localTime === "string" && /^\d{2}:\d{2}$/.test(data.localTime)
          ? data.localTime
          : undefined,
      tillName:
        typeof data.tillName === "string" &&
        data.tillName.length > 0 &&
        data.tillName.length <= 100
          ? data.tillName
          : undefined,
    };
  }
  if (data.type === RESTAURANT_NOTIFICATION_TYPES.onlineOrderRequested) {
    if (
      typeof data.storeId !== "string" ||
      data.storeId.length === 0 ||
      typeof data.onlineOrderId !== "string" ||
      data.onlineOrderId.length === 0
    ) {
      return null;
    }
    return {
      type: RESTAURANT_NOTIFICATION_TYPES.onlineOrderRequested,
      storeId: data.storeId,
      onlineOrderId: data.onlineOrderId,
      channel:
        data.channel === "TAKEAWAY" || data.channel === "DELIVERY"
          ? data.channel
          : undefined,
      itemCount: positiveInteger(data.itemCount),
    };
  }
  if (
    !isRestaurantNotificationType(data.type) ||
    data.type === RESTAURANT_NOTIFICATION_TYPES.dayClosed ||
    data.type === RESTAURANT_NOTIFICATION_TYPES.approvalUsed ||
    data.type === RESTAURANT_NOTIFICATION_TYPES.onlineOrderRequested ||
    typeof data.storeId !== "string" ||
    data.storeId.length === 0 ||
    typeof data.invoiceId !== "string" ||
    data.invoiceId.length === 0
  ) {
    return null;
  }
  return {
    type: data.type,
    storeId: data.storeId,
    invoiceId: data.invoiceId,
    itemCount: positiveInteger(data.itemCount),
    ticketCount: positiveInteger(data.ticketCount),
  };
}

export function restaurantNotificationRoute(
  notification: RestaurantNotificationData,
  locale: Locale,
): string {
  switch (notification.type) {
    // D156 — opening the approver's notice opens the bill the act was on.
    case RESTAURANT_NOTIFICATION_TYPES.itemsRequested:
    case RESTAURANT_NOTIFICATION_TYPES.itemsReady:
    case RESTAURANT_NOTIFICATION_TYPES.approvalUsed:
      return `/${locale}/pos/invoices/${encodeURIComponent(notification.invoiceId)}`;
    case RESTAURANT_NOTIFICATION_TYPES.paymentSubmitted:
      return `/${locale}/pos/invoices/${encodeURIComponent(notification.invoiceId)}#payments`;
    case RESTAURANT_NOTIFICATION_TYPES.kitchenTicketsCreated:
      return `/${locale}/kitchen`;
    case RESTAURANT_NOTIFICATION_TYPES.dayClosed:
      return `/${locale}/manage/day-close?date=${encodeURIComponent(notification.businessDate)}`;
    // The request is answered from the till's «التوصيل والتيك أواي» tab; a
    // till without that tab (classic look, no rights) falls back to Tables,
    // where the classic online-orders strip lives.
    case RESTAURANT_NOTIFICATION_TYPES.onlineOrderRequested:
      return `/${locale}/pos?view=delivery`;
  }
}

export type RestaurantNotificationQueryTarget = {
  queryKey: QueryKey;
  exact: boolean;
};

/**
 * P7.6 — the cashier's waiting lane reads its bills' plates in one read, so
 * whatever refreshes a bill here refreshes every lane read with it, as
 * `invalidateInvoiceMutation` does.
 */
const CASHIER_LANE_TARGET: RestaurantNotificationQueryTarget = {
  queryKey: queryKeys.cashierLane([]),
  exact: false,
};

export function restaurantNotificationQueryTargets(
  notification: RestaurantNotificationData,
): readonly RestaurantNotificationQueryTarget[] {
  switch (notification.type) {
    case RESTAURANT_NOTIFICATION_TYPES.itemsRequested:
      return [
        { queryKey: queryKeys.invoice(notification.invoiceId), exact: true },
        CASHIER_LANE_TARGET,
        { queryKey: queryKeys.grid, exact: true },
      ];
    case RESTAURANT_NOTIFICATION_TYPES.paymentSubmitted:
      return [
        { queryKey: queryKeys.invoice(notification.invoiceId), exact: true },
        CASHIER_LANE_TARGET,
        {
          queryKey: queryKeys.invoicePayments(notification.invoiceId),
          exact: true,
        },
      ];
    case RESTAURANT_NOTIFICATION_TYPES.kitchenTicketsCreated:
    case RESTAURANT_NOTIFICATION_TYPES.itemsReady:
      return [
        { queryKey: queryKeys.invoice(notification.invoiceId), exact: true },
        CASHIER_LANE_TARGET,
        { queryKey: ["restaurant", "kitchen-board"], exact: false },
      ];
    case RESTAURANT_NOTIFICATION_TYPES.dayClosed:
      return [
        {
          queryKey: queryKeys.dayClose(notification.businessDate),
          exact: true,
        },
        { queryKey: queryKeys.dayClose(null), exact: true },
      ];
    case RESTAURANT_NOTIFICATION_TYPES.approvalUsed:
      return [
        { queryKey: queryKeys.invoice(notification.invoiceId), exact: true },
        CASHIER_LANE_TARGET,
      ];
    // The cards and the badge both live under this prefix.
    case RESTAURANT_NOTIFICATION_TYPES.onlineOrderRequested:
      return [{ queryKey: queryKeys.onlineOrders, exact: false }];
  }
}

/** A foreground push or explicit notification open also refreshes its target. */
export async function invalidateRestaurantNotificationQueries(
  queryClient: QueryClient,
  notification: RestaurantNotificationData,
): Promise<void> {
  await Promise.all(
    restaurantNotificationQueryTargets(notification).map(({ queryKey, exact }) =>
      queryClient.invalidateQueries({ queryKey, exact }),
    ),
  );
}

export type NotificationOpenDependencies = {
  session: SellerSession;
  notification: RestaurantNotificationData;
  locale: Locale;
  queryClient: QueryClient;
  switchStore: (storeSlug: string) => Promise<void>;
  refreshSession: () => Promise<SellerSession | undefined>;
  recoverBinding: () => void;
  navigate: (path: string) => void;
  onUnavailable: () => void;
  onFailure: () => void;
};

/**
 * Open a trusted notification destination. A cross-store open changes the
 * server session first, clears the previous store's scoped caches, proves
 * the new binding through the session endpoint, then refreshes/navigates.
 */
export async function openRestaurantNotification({
  session,
  notification,
  locale,
  queryClient,
  switchStore,
  refreshSession,
  recoverBinding,
  navigate,
  onUnavailable,
  onFailure,
}: NotificationOpenDependencies): Promise<boolean> {
  if (notification.storeId !== session.seller.storeId) {
    const target = (session.availableStores ?? []).find(
      (store) =>
        store.storeId === notification.storeId &&
        store.storeKind === "RESTAURANT",
    );
    if (!target) {
      onUnavailable();
      return false;
    }
    const removeStoreScopedQueries = () =>
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== queryKeys.session[0],
      });
    const restoreOriginalBinding = async (): Promise<boolean> => {
      try {
        await switchStore(session.seller.storeSlug);
        removeStoreScopedQueries();
        const restored = await refreshSession();
        return restored?.seller.storeId === session.seller.storeId;
      } catch (error) {
        console.warn(
          "[restaurant-notifications] Original store restoration failed",
          error,
        );
        return false;
      }
    };
    try {
      await switchStore(target.storeSlug);
      removeStoreScopedQueries();
      const refreshed = await refreshSession();
      if (refreshed?.seller.storeId !== notification.storeId) {
        const restored = await restoreOriginalBinding();
        if (restored) onUnavailable();
        else recoverBinding();
        return false;
      }
    } catch (error) {
      console.warn(
        "[restaurant-notifications] Notification store switch failed",
        error,
      );
      // The request may have reached the server even when the browser saw a
      // network failure. Restore and prove the old binding before leaving the
      // user on the old route; otherwise reload through the locale root so no
      // UI can continue on an unverified server session.
      removeStoreScopedQueries();
      const restored = await restoreOriginalBinding();
      if (restored) onFailure();
      else recoverBinding();
      return false;
    }
  }

  await invalidateRestaurantNotificationQueries(queryClient, notification);
  navigate(restaurantNotificationRoute(notification, locale));
  return true;
}
